import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select/Select';
import { Button } from '@/shared/components/ui/Button/Button';
import { Input } from '@/shared/components/ui/Input/Input';
import { Switch } from '@/shared/components/ui/Switch/Switch';

import {
  KEY_VALUE_CELL_CLASSES,
  KEY_VALUE_CELL_LABEL_CLASSES,
  KEY_VALUE_RAW_VALUE_CLASSES,
  KEY_VALUE_SWITCH_ROW_CLASSES,
  KEY_VALUE_SWITCH_STATE_CLASSES,
} from '../structured.tokens';

import { type KeyValueValueCellProps } from './KeyValueValueCell.types';

/**
 * A row's value, drawn in whatever control its declared kind calls for.
 *
 * The mismatch branch is the interesting one. A stored value whose type
 * contradicts its declared kind is shown **verbatim and read-only**, with an
 * explicit "replace it" action beside it — never coerced (`"8"` quietly becoming
 * `8` is a data rewrite nobody asked for) and never dropped (that deletes a key
 * on the next unrelated keystroke). Until the operator decides, the value is
 * emitted exactly as it arrived.
 *
 * Presentation only (U5).
 */
export function KeyValueValueCell({
  row,
  index,
  size,
  options,
  placeholder,
  valueDir,
  isDisabled,
  isReadOnly,
  onTextChange,
  onBooleanChange,
  onAdoptKind,
  onBlur,
  registerControl,
  describedById,
}: KeyValueValueCellProps) {
  const { t } = useTranslation('uiKit');

  const label =
    row.key.trim() === ''
      ? t('keyValue.valueFor', { index: index + 1 })
      : t('keyValue.valueForNamed', { key: row.key });

  // Always "Value", never the entry's own label: below `sm` the row stacks and
  // the key cell is already showing that label directly above this one, so
  // repeating it printed the same words twice down a phone screen.
  const cellLabel = (
    <span aria-hidden className={KEY_VALUE_CELL_LABEL_CLASSES}>
      {t('keyValue.valueLabel')}
    </span>
  );

  if (row.isMismatched) {
    return (
      <div className={KEY_VALUE_CELL_CLASSES}>
        {cellLabel}
        <p dir="ltr" className={KEY_VALUE_RAW_VALUE_CLASSES}>
          {String(row.original)}
        </p>
        {isReadOnly || isDisabled ? null : (
          // Two words, not a sentence. The row's own message already says what
          // the value is stored as and what it should be; a button repeating it
          // could not fit its cell at three columns, and `buttonVariants` is
          // `whitespace-nowrap` — so it left the cell rather than wrapping.
          <Button
            ref={registerControl}
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={onAdoptKind}
          >
            {t('keyValue.replaceValue')}
          </Button>
        )}
      </div>
    );
  }

  if (row.kind === 'boolean') {
    const isOn = row.text === 'true';

    return (
      <div className={KEY_VALUE_CELL_CLASSES}>
        {cellLabel}
        <div className={KEY_VALUE_SWITCH_ROW_CLASSES}>
          <Switch
            ref={registerControl}
            aria-label={label}
            aria-describedby={describedById}
            // `role="switch"` inherits `aria-readonly` from checkbox, so the
            // state is announced rather than implied by a refusal nobody heard.
            aria-readonly={isReadOnly ? true : undefined}
            checked={isOn}
            disabled={isDisabled}
            onBlur={onBlur}
            onCheckedChange={onBooleanChange}
          />
          {/* The switch is not the only thing that says which way it is: a
              two-state control with no word beside it is a coin toss in a
              screenshot and in a low-vision reading alike. */}
          <span className={KEY_VALUE_SWITCH_STATE_CLASSES}>
            {isOn ? t('keyValue.booleanOn') : t('keyValue.booleanOff')}
          </span>
        </div>
      </div>
    );
  }

  if (row.kind === 'select') {
    const choices = row.entry?.options ?? options ?? [];

    return (
      <div className={KEY_VALUE_CELL_CLASSES}>
        {cellLabel}
        {/*
          `disabled` follows `isDisabled` ALONE. A read-only select stays live,
          focusable and openable, says `aria-readonly` (which `role="combobox"`
          supports), and has its choice refused — and ANNOUNCED — by the editor's
          one choke point. Disabling it here would be the exact defect the
          selection family shipped and had to undo: read-only rendered as
          unavailable, dropped from the tab order, and unable to say why.
        */}
        <Select value={row.text} disabled={isDisabled} onValueChange={onTextChange}>
          <SelectTrigger
            ref={registerControl}
            aria-label={label}
            aria-describedby={describedById}
            aria-invalid={row.issues.length > 0 ? true : undefined}
            aria-readonly={isReadOnly ? true : undefined}
            fieldSize={size}
            onBlur={onBlur}
          >
            <SelectValue placeholder={t('keyValue.selectPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {choices.map((choice) => (
              <SelectItem key={choice.value} value={choice.value}>
                {choice.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <div className={KEY_VALUE_CELL_CLASSES}>
      {cellLabel}
      <Input
        ref={registerControl}
        aria-label={label}
        aria-describedby={describedById}
        type={row.kind === 'number' ? 'number' : 'text'}
        inputMode={row.kind === 'number' ? 'numeric' : undefined}
        min={row.entry?.min}
        max={row.entry?.max}
        step={row.entry?.step}
        value={row.text}
        placeholder={row.entry?.placeholder ?? placeholder ?? t('keyValue.valueLabel')}
        disabled={isDisabled}
        readOnly={isReadOnly}
        isInvalid={row.isNotANumber || row.issues.length > 0}
        inputSize={size}
        // A number is read left to right in every language; prose follows the
        // interface, and a caller can force `ltr` for a URL or an identifier.
        dir={row.kind === 'number' ? 'ltr' : (row.entry?.valueDir ?? valueDir)}
        onBlur={onBlur}
        onChange={(event) => {
          onTextChange(event.target.value);
        }}
      />
    </div>
  );
}
